/**
 * File uploads with progress. `fetch` can't report upload progress, so this one helper uses XMLHttpRequest, with
 * the same rules as `request()`: same-origin cookies, the CSRF header, and `ApiError` for non-2xx answers.
 *
 *   const { data } = await upload<MediaDetail>("/api/v1/admin/media", form, (done) => setProgress(done));
 */

import { ApiError, toApiError } from "./errors";
import { readCookie, type ApiResponse } from "./http";

export function upload<T>(
  url: string,
  body: FormData,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<ApiResponse<T>> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    const token = readCookie("csrftoken");
    if (token) xhr.setRequestHeader("X-CSRFToken", token);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let data: unknown = null;
      if (xhr.responseText) {
        try {
          data = JSON.parse(xhr.responseText) as unknown;
        } catch {
          data = xhr.responseText;
        }
      }
      const headers = new Headers();
      for (const line of xhr
        .getAllResponseHeaders()
        .trim()
        .split(/[\r\n]+/)) {
        const at = line.indexOf(":");
        if (at > 0) headers.append(line.slice(0, at).trim(), line.slice(at + 1).trim());
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve({ data: data as T, status: xhr.status, headers });
      } else {
        reject(toApiError(xhr.status, data, headers.get("X-Request-ID")));
      }
    };
    const networkError = () =>
      reject(
        new ApiError({
          status: 0,
          type: "network_error",
          message: "Can't reach the server. Check your connection.",
        }),
      );
    xhr.onerror = networkError;
    xhr.onabort = () =>
      reject(new ApiError({ status: 0, type: "aborted", message: "The upload was cancelled." }));
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(body);
  });
}
